import { defineComponent as _defineComponent } from 'vue'
import { computed, ref, ShallowRef, useTemplateRef } from 'vue';
import { ExamplePrompt, type Message, USER_ROLE } from '@/models';
import axios from 'axios';
import markdownit from 'markdown-it';
import { store } from '../store';
import { AntSubscription } from '@/antService';
import ToastService from '@/toastService';
import PromptExamples from './PromptExamples.vue';
import { i18n } from '@/assets/i18n';

// define props

export default /*@__PURE__*/_defineComponent({
  __name: 'Chat',
  props: {
    token: { type: String, required: true },
    percent: { type: Number, required: true }
  },
  emits: ['onAnswer', 'onError'],
  setup(__props: any, { expose: __expose, emit: __emit }) {
  __expose();

const props = __props;

const md = markdownit({
  html: true,
  linkify: true,
  typographer: true
});

// define events
const emit = __emit;

const chat: ShallowRef = useTemplateRef('chat-list');

const loadingPercent = ref(0);

const userInput = ref(false);
const chatInput = ref(null);
let usage = -1;
let inputTimeout = -1;

const loadingStyle = computed(() => {
  return (
    'filter: blur(' +
    (10 - Math.round(loadingPercent.value / 10)) +
    'px);opacity:' +
    (0.009 * loadingPercent.value + 0.1).toFixed(2) +
    ';'
  );
});

/**
 * Estimates the energy usage for an answer, based on the number of output tokens. Samsi et al. estimated the energy usage with 3 - 4 Joule per token, which equals to ~0.001 Wh
 * @see Paper       Samsi et al. (2023): From Words to Watts: Benchmarking the Energy Costs of Large Language Model Inference. https://doi.org/10.48550/arXiv.2310.03003
 * @see Blogpost    gpt-oss uses around 8.3 Wh per 100 output token, which seems way too high https://huggingface.co/blog/sasha/gpt-oss-energy
 * @see YouTube     https://www.youtube.com/watch?v=zjG9TkfWd0o&t=143s
 * @param tokens    number of output tokens of the answer
 * @param tps       token per seconds generated, defaults to 70
 * @returns         an estimation of energy (in Wh), based on the number of output tokens
 */
function estimateEnergyUsage(tokens: number, tps = 70) {
  // Mac Studio M3Max uses up to 480W. That is 0.1333 Wh per second in full load (480 / 3600).
  // however, he measures 120W: https://www.youtube.com/watch?v=zjG9TkfWd0o&t=143s TODO: own measurements
  // we use the Watt per Token stored in ENV setup
  const whPerSecond = Number(store.connection.WATT_PER_TOKEN) / 3600;
  // With gpt-oss:120b, it generates around 70 token/s.
  const whPerToken = whPerSecond / tps;
  const estimate = tokens * whPerToken;
  return estimate;
  // old samsi et al estimate: return (tokens * 3.5) / 3600; // 3.5 joules per token; 3600 J = 1 Wh
}

/**
 * Estimates the energy usage before knowing the answer
 * @returns         an estimation of energy (in Wh)
 */
function preEstimateUsage() {
  return usage > 1 ? usage : 1;
}

/**
 * Checks if a message is currently the last
 * @param index   the index of the message
 * @returns       TRUE if the message is the last in the array
 *                FALSE if there are more messages after it in the array
 */
function isLastMessage(index: number): boolean {
  return store.getVisibleMessages().length - index === 1;
}

/**
 * Sends a message to the model, with all previous messages as context
 */
function send() {
  if (store.textInput == '') return;

  const message: Message = {
    role: USER_ROLE.USER,
    content: store.textInput,
    loading: false
  };
  loadingPercent.value = 0;

  store.chatMessages.push(message);

  type API_BODY = {
    model: string;
    messages: Message[];
    reasoning?: {
      effort: string;
    };
    'background-tasks'?: {
      follow_up_generation: boolean;
    };
  };

  const body: API_BODY = {
    model: store.connection.MODEL,
    messages: [...store.chatMessages],
    reasoning: {
      effort: store.connection.REASONING
    },
    'background-tasks': {
      follow_up_generation: false
    }
  };

  // chatGPT does not like these params
  if (!store.connection.REASONING) {
    delete body.reasoning;
    delete body['background-tasks'];
  }

  store.textInput = '';

  const answerMessage = {
    role: USER_ROLE.AI,
    content: '...',
    loading: true,
    percent: 0
  };

  store.chatMessages.push(answerMessage);
  usage = -1;

  chat.value.lastElementChild?.scrollIntoView({ behavior: 'smooth', block: 'end' });

  const time = Date.now();
  Promise.all([
    ToastService.startToast().then(() => {
      store.startAndSubscribe(usage === -1 ? preEstimateUsage() : usage, (ant: AntSubscription) => {
        answerMessage.percent = Math.min(ant.percent, 100);
        loadingPercent.value = Math.min(ant.percent, 100);

        ToastService.progressToast(
          answerMessage.percent,
          answerMessage.content !== '...',
          ant.value
        );
        if (ant.percent >= 100 && usage > 0) {
          ToastService.energyToast(usage);
          usage = -1;
        }
      });
    }),
    axios
      .post(store.connection.BASE_URL + store.connection.ENDPOINT, body, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + props.token,
          'Cache-Control': 'no-cache, no-store'
        }
      })
      .then((result) => {
        const duration = Math.round((Date.now() - time) / 1000);
        usage = estimateEnergyUsage(
          result.data.usage.completion_tokens,
          result.data.usage.generation_tokens_per_second
        );
        store.setTarget(usage);

        console.log(
          'Energie verbraucht: ' +
            usage.toFixed(2) +
            ' Wh in ' +
            duration +
            ' Sekunden. \nDas benötigt eine Durchschnittsleistung von ' +
            Math.round((3600 * usage) / duration) +
            ' Watt.'
        );

        answerMessage.content = (result.data.choices[0].message.content as string).replaceAll(
          'ß',
          'ss'
        );
        answerMessage.loading = false;

        // we need to do this, or vue won't detect the update...
        store.chatMessages.pop();
        store.chatMessages.push(answerMessage);

        chat.value.lastElementChild?.scrollIntoView({ behavior: 'smooth', block: 'end' });
        emit('onAnswer', answerMessage);
      })
      .catch((e) => {
        console.error(e);
        ToastService.abort();
        emit('onError', JSON.stringify(e, null, 2));
      })
  ]);
}

/**
 * Sets a 1 second timeout in which we don't listen to hotkey inputs
 */
function inputting() {
  userInput.value = true;
  if (inputTimeout > 0) {
    window.clearTimeout(inputTimeout);
  }
  inputTimeout = window.setTimeout(() => {
    userInput.value = false;
  }, 1000);
}

function setPrompt(prompt: ExamplePrompt) {
  if (!store.chatMessages[store.chatMessages.length - 1]?.loading) {
    store.textInput = prompt[store.lang];
    usage = prompt.estimateFactor;
    store.setTarget(usage);
    (chatInput.value as any)?.focus();
  }
}

const __returned__ = { props, md, emit, chat, loadingPercent, userInput, chatInput, get usage() { return usage }, set usage(v) { usage = v }, get inputTimeout() { return inputTimeout }, set inputTimeout(v) { inputTimeout = v }, loadingStyle, estimateEnergyUsage, preEstimateUsage, isLastMessage, send, inputting, setPrompt, get store() { return store }, PromptExamples, get i18n() { return i18n } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})